import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IhkEducationVoucherService } from '../../../services/ihkeducationvoucher.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';

import { TableModule } from 'primeng/table';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';
import { ListSettingsService } from 'src/app/services/listsettings.service';
import { catchError, map, Observable, tap } from 'rxjs';
import { ListItem } from 'src/app/models/ListItem.model';
import { NotificationService } from 'src/app/services/notification.service';

// Minimaler Typ für die Mapping-Funktion, um Daten zu separieren.
// Mit Omit kann man einen neuen Typen erstellen und lässt in diesem Fall id und value weg.
type ListItemMinimal = Omit<ListItem, 'id' | 'value'>;

@Component({
  standalone: true,
  selector: 'app-documents-dialog',
  templateUrl: './dokuments-dialog.component.html',
  styleUrl: './dokuments-dialog.component.scss',
  imports: [
    CommonModule,
    TableModule,
    DropdownModule,
    TooltipModule,
    SkeletonModule,
  ],
})
export class DocumentsDialogComponent implements OnInit {
  documents: any[] = [];
  dataLoaded = false;

  voucherTypeOptions: ListItem[] = [];

  constructor(
    private voucherSrv: IhkEducationVoucherService,
    public ref: DynamicDialogRef,
    private readonly listSettings: ListSettingsService,
    private readonly notificationService: NotificationService,
  ) {}

  voucherTypeData: Observable<Record<string, ListItemMinimal>>;

  private loadVoucherType(listName: string) {
    return this.listSettings.getListByName(listName).pipe(
      tap((list) => {
        this.voucherTypeOptions = list;
      }),
      map((list: ListItem[]) => {
        const ListMap: Record<string, ListItemMinimal> = {};
        list.forEach((item) => {
          ListMap[item.value] = {
            icon: item.icon ?? 'pi pi-question-circle',
            color: item.color ?? '#6c757d',
            label: item.label,
          };
        });
        return ListMap;
      }),
      catchError((err) => {
        this.voucherTypeOptions = [];
        this.notificationService.error('Fehler beim Laden der Voucher-Typen');
        throw err;
      }),
    );
  }

  ngOnInit(): void {
    this.loadDocuments();
    this.voucherTypeData = this.loadVoucherType('Dokumenten Upload Typ');
  }

  loadDocuments(): void {
    this.voucherSrv.getAllDocuments().subscribe({
      next: (res) => {
        this.documents = res;
        this.dataLoaded = true;
      },
      error: (err) => {
        console.error('Dokumente konnten nicht geladen werden', err);
        this.dataLoaded = true;
      },
    });
  }

  getTypeStyle(
    voucherTypeStyles: Record<string, ListItemMinimal>,
    type: string,
  ) {
    return (
      voucherTypeStyles[type] ?? {
        icon: 'pi pi-question-circle',
        color: '#6c757d',
        label: type || 'Unbekannt',
      }
    );
  }

  getInitials(name: string): string {
    if (!name) return '';
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase();
  }

  getUserColor(name: string): string {
    if (!name) return '#ccc';
    const hash = Array.from(name).reduce(
      (acc, ch) => acc + ch.charCodeAt(0),
      0,
    );
    return `hsl(${hash % 360}, 70%, 50%)`;
  }

  customSort(event: any) {
    const field = event.field;

    event.data.sort((a: any, b: any) => {
      let v1 = a[field];
      let v2 = b[field];

      if (v1 == null) v1 = '';
      if (v2 == null) v2 = '';

      if (typeof v1 === 'string') {
        v1 = v1.toLowerCase();
        v2 = v2.toLowerCase();
      }

      return (v1 > v2 ? 1 : v1 < v2 ? -1 : 0) * event.order;
    });
  }
  private stripHtml(html: string | null | undefined): string {
    if (!html) return '';
    return html
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  getPlainText(html: string | null | undefined): string {
    return this.stripHtml(html);
  }

  getShortText(
    html: string | null | undefined,
    maxLength: number = 80,
  ): string {
    const text = this.stripHtml(html);
    if (text.length <= maxLength) {
      return text;
    }
    return text.slice(0, maxLength) + '…';
  }
}
